import React from 'react';
import { Spinner } from 'react-bootstrap';
import { Redirect } from 'react-router-dom';
import { Form, FormGroup, ValidationMessage } from '~/components/Form';
import { AuthContext, AuthContextType, ServicesContext } from '~/services';

interface TwoFactorAuthType {
    options: {
        type: string;
        label: string;
        value: string;
    }[];
    // eslint-disable-next-line babel/camelcase
    trusted_computer_enabled: boolean;
    // eslint-disable-next-line babel/camelcase
    last_requested_option_value: string;
}
interface MfaSelectProps {
    submitAction: (x: boolean) => void;
}
export function MfaSelect({ submitAction }: MfaSelectProps) {
    const servicesContext = React.useContext(ServicesContext);
    const { authenticated } = React.useContext(AuthContext) as AuthContextType;
    const twoFactorAuth: TwoFactorAuthType = servicesContext.user.getMfa();
    const [loading, setLoading] = React.useState(false);

    const [error, setError] = React.useState(null);
    const [mfaoption, setMfaOption] = React.useState(() => {
        const lastOption = twoFactorAuth?.options.find(
            (option) => option.value === twoFactorAuth.last_requested_option_value
        );
        return lastOption ? lastOption.value : null;
    });

    async function handleSubmit() {
        try {
            setLoading(true);
            await servicesContext.user.mfa(mfaoption);
            submitAction(true);
            setLoading(false);
        } catch (e) {
            setError(e.message);
            setLoading(false);
        }
    }

    function getRedirects() {
        if (window.app.reset_password && window.app.reset_password.ready) {
            servicesContext.user.setResetEndpoint(window.app.reset_password.endpoint);
            return <Redirect to="/reset" />;
        }
        if (authenticated) {
            return <Redirect to="/dashboard" />;
        }
        return false;
    }

    return (
        getRedirects() || (
            <Form onSubmit={handleSubmit} values={{ mfaoption }}>
                {servicesContext.user.hasFeatureToggle('trusted_computer') && (
                    <>
                        <p>We don&apos;t recognize the computer you&apos;re using.</p>
                        <p>
                            This may have happened because you&apos;re using a device you don&apos;t
                            usually use or you cleared the browser&apos;s cookies.
                        </p>
                    </>
                )}
                <p>
                    For your security, we need to verify your identity before you can sign in to
                    your account.
                </p>
                <p>Please choose how you want to receive your temporary identification code.</p>
                <FormGroup htmlFor="mfaoption" className="mb-3">
                    {twoFactorAuth.options.map((option) => (
                        <div className="row" key={option.label}>
                            <div
                                className="col-5 text-end"
                                style={{
                                    textTransform: 'capitalize',
                                }}
                            >
                                {option.type}
                            </div>
                            <div className="col-7">
                                <div className="form-check">
                                    <input
                                        type="radio"
                                        name="mfaoption"
                                        checked={option.value === mfaoption}
                                        className="form-check-input"
                                        id={option.value}
                                        value={option.value}
                                        required
                                        onChange={(e) => setMfaOption(e.target.value)}
                                    />
                                    <label htmlFor={option.value} className="form-check-label">
                                        {option.label}
                                    </label>
                                </div>
                            </div>
                        </div>
                    ))}
                    <ValidationMessage type="required" className="col-12 text-center mt-3">
                        Please select an authentication option.
                    </ValidationMessage>
                </FormGroup>
                <div className="row mt-3">
                    <div className="col text-end">
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={!mfaoption || loading}
                        >
                            {loading && (
                                <Spinner
                                    animation="border"
                                    variant="light"
                                    size="sm"
                                    role="status"
                                    className="me-1"
                                />
                            )}
                            {mfaoption?.includes('google') ? 'Continue' : 'Send Code'}
                        </button>
                    </div>
                </div>
                {error && (
                    <div className="row mt-3">
                        <div className="col-10 offset-1 text-center">{error}</div>
                    </div>
                )}
            </Form>
        )
    );
}
